import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import api from '@/services/api';
import { useTerritoryStore } from '@/stores/territoryStore';

/**
 * HeatIslandSimulator — panneau de simulation des leviers d'atténuation des îlots de chaleur.
 *
 * API : POST /simulations
 *   body: { territory_code, name, type: 'heat_island', parameters: { grid_size } }
 *   réponse: { id, status: 'completed', ... }
 *   Les SimulationResult par maille sont stockés en base — GET /simulations/{id}.
 *
 * ⚠ DEMO : Les réductions ΔT par levier affichées sont des **valeurs indicatives de
 * démonstration** issues de la littérature scientifique (ordre de grandeur ICU, non mesurées
 * sur le territoire). En production, le moteur FEM/CFD recalculerait sur la géométrie réelle.
 * Sources : Oke et al. (2017), ADEME (2020), CNRS/LATMOS — valeurs médianes Europe tempérée.
 */

// Réductions ΔT indicatives par levier — DEMO (°C, relatifs, non mesurés sur le territoire)
const LEVIER_MODELS = [
  {
    id: 'add_trees',
    label: 'Planter des arbres',
    desc: '+20 % couvert végétal · ombre + évapotranspiration',
    delta_t: -2.1, // DEMO °C indicatif — source : Oke et al. 2017
  },
  {
    id: 'cool_roofs',
    label: 'Toitures claires',
    desc: "Augmenter l'albédo des toitures existantes",
    delta_t: -1.3, // DEMO °C indicatif — source : ADEME 2020
  },
  {
    id: 'desealing',
    label: 'Désimperméabiliser',
    desc: 'Retirer 10 % de surfaces imperméables',
    delta_t: -0.9, // DEMO °C indicatif — source : CNRS/LATMOS
  },
] as const;

type LevierId = (typeof LEVIER_MODELS)[number]['id'];

// Steps de résolution de maille (mètres)
const GRID_STEPS = [25, 50, 100, 200] as const;
type GridSize = (typeof GRID_STEPS)[number];

const GRID_LABELS: Record<GridSize, string> = {
  25:  'Très fine — haute précision, temps de calcul élevé',
  50:  'Fine — recommandée pour la décision',
  100: 'Médium — équilibre précision/performance',
  200: 'Grossière — aperçu rapide',
};

/** Réponse minimale de POST /simulations pour le type heat_island. */
interface SimulationApiResponse {
  id: number;
  status: string;
}

/**
 * Résultat de simulation retourné au composant parent.
 *
 * `_demo: true` signale que les ΔT par levier sont calculés côté client à partir de
 * valeurs indicatives de littérature — ils NE représentent PAS une mesure sur le territoire.
 */
export interface HeatIslandResult {
  /** ID de la simulation créée en base (voir GET /simulations/{sim_id}). */
  sim_id: number;
  grid_size: GridSize;
  /**
   * ΔT cumulé — DEMO : somme des ΔT indicatifs des leviers sélectionnés.
   * Valeur négative (refroidissement). Non mesurée.
   */
  delta_t_total: number;
  by_levier: { id: string; label: string; delta_t: number }[];
  readonly _demo: true;
}

interface HeatIslandSimulatorProps {
  onResult: (r: HeatIslandResult | null) => void;
}

export function HeatIslandSimulator({ onResult }: HeatIslandSimulatorProps) {
  const { activeTerritory } = useTerritoryStore();
  const [gridSize, setGridSize] = useState<GridSize>(50);
  const [selectedLeviers, setSelectedLeviers] = useState<LevierId[]>([]);
  const [simResult, setSimResult] = useState<HeatIslandResult | null>(null);

  const sim = useMutation<SimulationApiResponse, Error, void>({
    mutationFn: async () => {
      const { data } = await api.post<SimulationApiResponse>('/simulations', {
        territory_code: activeTerritory?.code_insee,
        name: `sim-heat_island-${Date.now()}`,
        type: 'heat_island',
        parameters: { grid_size: gridSize },
      });
      return data;
    },
    onSuccess: (resp) => {
      // Les SimulationResult sont en base. On calcule les ΔT DEMO par levier côté client.
      const by_levier = LEVIER_MODELS
        .filter((l) => selectedLeviers.includes(l.id))
        .map((l) => ({ id: l.id, label: l.label, delta_t: l.delta_t }));

      const delta_t_total = by_levier.reduce((sum, l) => sum + l.delta_t, 0);

      const result: HeatIslandResult = {
        sim_id: resp.id,
        grid_size: gridSize,
        delta_t_total,
        by_levier,
        _demo: true,
      };
      setSimResult(result);
      onResult(result);
    },
  });

  const toggleLevier = (id: LevierId) => {
    setSelectedLeviers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
    sim.reset();
    setSimResult(null);
    onResult(null);
  };

  const handleGridChange = (step: GridSize) => {
    setGridSize(step);
    sim.reset();
    setSimResult(null);
    onResult(null);
  };

  const canSimulate = selectedLeviers.length > 0 && !sim.isPending;

  return (
    <div className="flex flex-col gap-3 rounded-panel border border-line bg-white p-[18px] shadow-card h-full overflow-y-auto">
      <div>
        <h3 className="font-display text-[15px] font-bold text-ink">
          Simulation d'atténuation
        </h3>
        <p className="mt-0.5 text-xs text-faint">
          Combiner des leviers pour réduire l'effet d'îlot
        </p>
      </div>

      {/* ── Résolution de maille ─────────────────────────────────────────── */}
      <div>
        <p className="mb-2 text-[11.5px] font-semibold text-faint">Résolution de maille</p>
        <div className="flex flex-wrap gap-1.5">
          {GRID_STEPS.map((step) => (
            <button
              key={step}
              type="button"
              onClick={() => handleGridChange(step)}
              className={`rounded-[8px] border px-2.5 py-1 text-[12px] font-semibold transition ${
                gridSize === step
                  ? 'border-navy bg-navy text-white'
                  : 'border-line bg-white text-muted hover:border-navy hover:text-ink'
              }`}
            >
              {step} m
            </button>
          ))}
        </div>
        <p className="mt-1 text-[11px] text-faint">{GRID_LABELS[gridSize]}</p>
      </div>

      {/* ── Leviers d'atténuation ────────────────────────────────────────── */}
      <div>
        <p className="mb-2 text-[11.5px] font-semibold text-faint">
          Leviers d'atténuation
        </p>
        <div className="flex flex-col gap-2">
          {LEVIER_MODELS.map((levier) => {
            const on = selectedLeviers.includes(levier.id);
            return (
              <button
                key={levier.id}
                type="button"
                onClick={() => toggleLevier(levier.id)}
                aria-pressed={on}
                className={`flex items-center gap-3 rounded-[11px] border px-3 py-2.5 text-left transition ${
                  on
                    ? 'border-[#9bb8ee] bg-[#eef4ff]'
                    : 'border-line bg-white hover:border-muted'
                }`}
              >
                {/* Checkbox (couleur + forme) */}
                <span
                  aria-hidden="true"
                  className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md border ${
                    on ? 'border-navy bg-navy' : 'border-[#cbd3e2] bg-white'
                  }`}
                >
                  {on && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3"
                      aria-hidden="true"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span className="flex-1">
                  <span className="block text-[13.5px] font-semibold text-ink">
                    {levier.label}
                  </span>
                  <span className="block text-[11.5px] text-faint">{levier.desc}</span>
                </span>
                {/* ΔT de référence (littérature ADEME / Oke) — ordre de grandeur */}
                <span
                  className="flex-shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold"
                  style={{ background: '#eaf2fb', color: '#2c3a78' }}
                  title="Réduction ΔT estimée d'après la littérature scientifique (ADEME, Oke et al.) — ordre de grandeur, non mesuré sur le territoire"
                >
                  réf. −{levier.delta_t} °C
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Bouton Lancer ────────────────────────────────────────────────── */}
      <button
        type="button"
        disabled={!canSimulate}
        onClick={() => sim.mutate()}
        className="mt-1 flex h-[46px] w-full items-center justify-center gap-2 rounded-[11px] text-sm font-bold text-white transition disabled:cursor-not-allowed"
        style={{ background: canSimulate ? '#e1000f' : '#cbd3e2' }}
      >
        {sim.isPending ? (
          <>
            <Loader2 size={16} className="animate-spin" aria-hidden="true" />
            Analyse en cours…
          </>
        ) : (
          "Lancer l'analyse"
        )}
      </button>

      {sim.isError && (
        <p className="text-xs text-[#e1000f]" role="alert">
          Erreur lors de la simulation. Veuillez réessayer.
        </p>
      )}

      {/* ── Résultats ────────────────────────────────────────────────────── */}
      {simResult && (
        <div className="rounded-[13px] border border-[#cfe7d8] bg-gradient-to-b from-[#eef8f1] to-[#e3f3ea] p-[15px]">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-[#1f8a5b]">
                ΔT combiné estimé
              </div>
              <div className="font-display text-[28px] font-extrabold leading-none text-[#1f8a5b]">
                {simResult.delta_t_total.toFixed(1)} °C
              </div>
              {/* DEMO disclaimer — explicite */}
              <div className="mt-0.5 text-[10px] text-[#5b6480]">
                Valeur indicative DEMO — non mesurée
              </div>
            </div>
            <div className="text-right">
              <div className="text-[11px] text-[#5b6480]">Maille</div>
              <div className="font-display text-[18px] font-bold text-ink">
                {simResult.grid_size} m
              </div>
            </div>
          </div>

          {/* Détail par levier */}
          <div className="mt-3 flex flex-col gap-1">
            {simResult.by_levier.map((l) => (
              <div key={l.id} className="flex justify-between text-[11.5px]">
                <span className="text-[#1f6b48]">{l.label}</span>
                <span className="font-mono font-semibold text-[#1f8a5b]">
                  {l.delta_t.toFixed(1)} °C (DEMO)
                </span>
              </div>
            ))}
          </div>

          <p className="mt-2 text-[10.5px] text-[#5b6480]">
            Sim #{simResult.sim_id} · Résultats maille : GET /simulations/
            {simResult.sim_id}
          </p>
        </div>
      )}

      {/* Note DEMO globale */}
      <p className="text-[10.5px] text-faint">
        ΔT indicatifs issus de la littérature scientifique (ICU Europe tempérée). Simulation
        réelle : modèle FEM/CFD sur géométrie bâtie.
      </p>
    </div>
  );
}
